/**
 * Semantic theme tokens — the single source of truth for the app's colors/font.
 *
 * Six color roles a researcher can set per study (Diapason "Theme" section,
 * stored as a THEME study_config; per-project defaults in src/projects/<slug>.ts):
 *   primary    — CHROME, painted directly: header wave, tab bar, sheet/menu
 *                toolbars, back pill. What the researcher picks is what the
 *                biggest surfaces show. A STRONG emphasis tone is DERIVED from
 *                it (deriveEmphasis) for progress fills, slider fills/handles,
 *                completion feedback and emphasized inks — those need
 *                saturation a pastel chrome pick can't provide
 *   secondary  — ACTIONS + ATTENTION: CTA buttons (Button/FormButton), badges,
 *                notification pills, selected filter chips
 *   accent     — INTERACTIVE ANSWERS: Ionic form controls (radios/checkboxes/
 *                toggles), question selection states, links/arrows on cards
 *                (sliders follow the emphasis tone, like progress bars)
 *   background — page background
 *   surface    — cards, input fields, sheets
 *   text       — default ink
 *
 * Everything else (contrast/on-colors, Ionic shade/tint variants) is DERIVED
 * here so a theme author only ever picks the six base colors, and can't produce
 * unreadable text on buttons.
 *
 * Application is ONE mechanism: applyThemeTokens() writes CSS custom properties
 * on <html> — the Ionic variables map from them and the styled-components theme
 * reads the same values — ending the historical split where Ionic and
 * styled-components each had their own hardcoded palette.
 */

import type { LegacyPalette } from "./legacyPalettes";

export interface ThemeColorTokens {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  surface: string;
  text: string;
}

/** Header edge. Each edge also brings its page decoration (wave → bubbles,
 *  clouds → clouds, round → pale circles, flat → none) and the card shape
 *  family (flat = sober, the others = round) — see applyThemeTokens. */
export const HEADER_SHAPES = ["wave", "flat", "round", "clouds"] as const;
export type HeaderShape = (typeof HEADER_SHAPES)[number];
export const NAVBAR_STYLES = ["floating", "full"] as const;
export type NavbarStyle = (typeof NAVBAR_STYLES)[number];

/** Researcher layout options (THEME shape block). Absent key = the default
 *  look (wave header, floating navbar). */
export interface ThemeShape {
  header?: HeaderShape;
  navbar?: NavbarStyle;
}

export interface ThemeTokens {
  colors: ThemeColorTokens;
  /** Header/navbar shape options; applied as data attributes on <html>. */
  shape?: ThemeShape;
  /** Body/text font family name, e.g. `"Outfit"`. Undefined = platform default. */
  font?: string;
  /** Title/heading font (contract key "font_title"), e.g. `"Quicksand"`.
   *  Undefined = same as the text font. */
  fontTitle?: string;
}

/** Dark ink used when a color is light enough to need dark text on it. */
const DARK_CONTRAST = "#041723";
const LIGHT_CONTRAST = "#ffffff";
const FALLBACK_FONT_STACK = `"Roboto", sans-serif`;

const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  let h = hex.replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

const rgbToHex = ({ r, g, b }: { r: number; g: number; b: number }) =>
  `#${[r, g, b].map((v) => clamp(v).toString(16).padStart(2, "0")).join("")}`;

export const toRgbString = (hex: string): string => {
  const { r, g, b } = hexToRgb(hex);
  return `${r}, ${g}, ${b}`;
};

/** WCAG relative luminance. */
function luminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  const lin = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** White or dark ink, whichever reads better on the given color. */
export function contrastOn(hex: string): string {
  const whiteRatio = 1.05 / (luminance(hex) + 0.05);
  const darkRatio = (luminance(hex) + 0.05) / (luminance(DARK_CONTRAST) + 0.05);
  return whiteRatio >= darkRatio ? LIGHT_CONTRAST : DARK_CONTRAST;
}

/** Ionic-style pressed/hover variants derived from the base color. */
export function shade(hex: string, amount = 0.12): string {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHex({ r: r * (1 - amount), g: g * (1 - amount), b: b * (1 - amount) });
}

export function tint(hex: string, amount = 0.1): string {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHex({
    r: r + (255 - r) * amount,
    g: g + (255 - g) * amount,
    b: b + (255 - b) * amount,
  });
}

function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const { r, g, b } = hexToRgb(hex);
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = Math.min(1, d / (1 - Math.abs(2 * l - 1)));
  let h: number;
  if (max === rn) h = ((gn - bn) / d) % 6;
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  h *= 60;
  if (h < 0) h += 360;
  return { h, s, l };
}

function hslToHex(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let rn = 0;
  let gn = 0;
  let bn = 0;
  if (h < 60) [rn, gn, bn] = [c, x, 0];
  else if (h < 120) [rn, gn, bn] = [x, c, 0];
  else if (h < 180) [rn, gn, bn] = [0, c, x];
  else if (h < 240) [rn, gn, bn] = [0, x, c];
  else if (h < 300) [rn, gn, bn] = [x, 0, c];
  else [rn, gn, bn] = [c, 0, x];
  return rgbToHex({ r: (rn + m) * 255, g: (gn + m) * 255, b: (bn + m) * 255 });
}

/**
 * Strong emphasis tone of a color: same hue, lightness pinned to a saturated
 * mid tone. Primary is picked as the chrome color (often a pastel — what you
 * see on the header is what you picked); progress fills and emphasized inks
 * need a stronger tone of the same hue, derived through this.
 *
 * Tuned so Mental+'s pastel #94f1ef reproduces its historical strong teal
 * #2db6b4 (derivation gives #2cb9b6). Near-gray inputs skip the saturation
 * scaling (hue is meaningless at zero saturation): the tone just darkens to
 * a mid gray.
 *
 * Mirrored in Diapason's ThemeSection preview — keep the two in sync.
 */
export function deriveEmphasis(hex: string): string {
  const EMPHASIS_LIGHTNESS = 0.45;
  const SATURATION_FACTOR = 0.8;
  const { h, s } = hexToHsl(hex);
  const scaled = s < 0.05 ? s : s * SATURATION_FACTOR;
  return hslToHex(h, scaled, EMPHASIS_LIGHTNESS);
}

/** Blend of two colors: weight 0 = a, 1 = b. */
export function mixHex(a: string, b: string, weight: number): string {
  const x = hexToRgb(a);
  const y = hexToRgb(b);
  return rgbToHex({
    r: x.r + (y.r - x.r) * weight,
    g: x.g + (y.g - x.g) * weight,
    b: x.b + (y.b - x.b) * weight,
  });
}

/** WCAG contrast ratio between two colors (1 = identical, 21 = black/white). */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * The color, darkened (same hue and saturation, lower lightness) until it reads
 * at >= minRatio on every given ground. A pastel chrome pick (Mental+ #94f1ef)
 * becomes a deep teal ink; a color that is already readable comes back as is.
 */
export function inkOn(hex: string, grounds: string[], minRatio = 4.5): string {
  const { h, s, l } = hexToHsl(hex);
  const readable = (color: string) =>
    grounds.every((ground) => contrastRatio(color, ground) >= minRatio);
  let lightness = l;
  let out = hex;
  while (!readable(out) && lightness > 0.04) {
    lightness -= 0.01;
    out = hslToHex(h, s, lightness);
  }
  return out;
}

/**
 * Page tint: the header (chrome) color fading into the page background, so the
 * header flows into the page instead of sitting on plain white. A dark chrome
 * color is used much more lightly than a pastel one, so the tint always stays
 * a light wash.
 */
export function derivePageTint(colors: ThemeColorTokens): { top: string; mid: string } {
  const darkChrome = contrastOn(colors.primary) === LIGHT_CONTRAST;
  return {
    top: mixHex(colors.primary, colors.background, darkChrome ? 0.86 : 0.3),
    mid: mixHex(colors.primary, colors.background, darkChrome ? 0.94 : 0.68),
  };
}

/** Title weight that suits each theme title font (rounded Nunito reads best
 *  heavier); anything else uses bold. Only weights the font loaders ship. */
const HEADING_WEIGHT_BY_FONT: Record<string, number> = { Nunito: 800 };

const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const COLOR_ROLES: (keyof ThemeColorTokens)[] = [
  "primary",
  "secondary",
  "accent",
  "background",
  "surface",
  "text",
];

/**
 * Parse a THEME study_config value (the raw JSON string stored in the row)
 * into ThemeTokens. Returns null — never throws — on anything that doesn't
 * satisfy the contract: all six colors present as 3- or 6-digit hex
 * (case-insensitive). `font` is optional and tolerated as any string (the
 * allowlist is enforced by Diapason at authoring time; fontStack() falls back
 * gracefully at render time). Unknown keys are ignored.
 */
export function parseThemeTokens(value: unknown): ThemeTokens | null {
  if (typeof value !== "string" || value.trim() === "") return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch (e) {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;

  const { colors, font, font_title, shape } = parsed as {
    colors?: unknown;
    font?: unknown;
    font_title?: unknown;
    shape?: unknown;
  };
  if (typeof colors !== "object" || colors === null) return null;

  const validated = {} as ThemeColorTokens;
  for (const role of COLOR_ROLES) {
    const hex = (colors as Record<string, unknown>)[role];
    if (typeof hex !== "string" || !HEX_COLOR_RE.test(hex.trim())) return null;
    validated[role] = hex.trim();
  }

  const tokens: ThemeTokens = { colors: validated };
  if (typeof font === "string" && font.trim() !== "") tokens.font = font.trim();
  if (typeof font_title === "string" && font_title.trim() !== "") {
    tokens.fontTitle = font_title.trim();
  }
  // Tolerant like the rest of this parser: unknown/invalid shape entries are
  // dropped (defaults apply) rather than rejecting the whole theme.
  if (typeof shape === "object" && shape !== null && !Array.isArray(shape)) {
    const s = shape as Record<string, unknown>;
    const parsedShape: ThemeShape = {};
    if (
      typeof s.header === "string" &&
      (HEADER_SHAPES as readonly string[]).includes(s.header)
    ) {
      parsedShape.header = s.header as HeaderShape;
    }
    if (
      typeof s.navbar === "string" &&
      (NAVBAR_STYLES as readonly string[]).includes(s.navbar)
    ) {
      parsedShape.navbar = s.navbar as NavbarStyle;
    }
    if (Object.keys(parsedShape).length) tokens.shape = parsedShape;
  }
  return tokens;
}

/**
 * Bridge a token theme onto the legacy styled-components palette shape, so a
 * researcher-authored THEME also drives the ~40 styled consumers still reading
 * theme.primary.main / theme.secondary.* (see ThemeContext).
 *
 * The legacy names predate the role semantics above, so they are bridged by
 * WHAT THE CONSUMERS ARE, not by name: legacy `primary.main` readers are
 * links/active states/interactive text -> accent; legacy `secondary.*`
 * readers are soft tinted panels (consent rows, story modal, invitation
 * pills) -> surface, which visually matches the pastel they were designed on.
 */
export function legacyShapeFromTokens(tokens: ThemeTokens): LegacyPalette {
  const { colors } = tokens;
  return {
    primary: {
      main: colors.accent,
      background: colors.surface,
      contrast: contrastOn(colors.accent),
    },
    secondary: {
      main: colors.surface,
      background: colors.surface,
      contrast: contrastOn(colors.surface),
    },
    svg: {
      verified: "verified-green",
      finished: "finished-green",
    },
  };
}

/** 'System' = the OS's own UI font — nothing to download. */
const SYSTEM_FONT_STACK = `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;

/**
 * CSS font-family stack for a theme font name.
 *
 * No font declared -> plain Roboto/sans-serif fallback. Deliberately NOT
 * Ciutadella Rounded: it is a commercial font licensed for Mental+ only, so a
 * project that doesn't explicitly declare it must never fall back to it
 * (mentalplus declares font in src/projects/mentalplus.ts, so its stack is
 * unchanged). Loadable families are fetched by loadThemeFont() (fonts.ts).
 */
export const fontStack = (font?: string): string => {
  if (!font) return FALLBACK_FONT_STACK;
  if (font === "System") return SYSTEM_FONT_STACK;
  return `"${font.replace(/"/g, "")}", ${FALLBACK_FONT_STACK}`;
};

/** Write one Ionic color (base + the five derived vars Ionic expects). */
function setIonColor(root: HTMLElement, name: string, hex: string) {
  const contrast = contrastOn(hex);
  root.style.setProperty(`--ion-color-${name}`, hex);
  root.style.setProperty(`--ion-color-${name}-rgb`, toRgbString(hex));
  root.style.setProperty(`--ion-color-${name}-contrast`, contrast);
  root.style.setProperty(`--ion-color-${name}-contrast-rgb`, toRgbString(contrast));
  root.style.setProperty(`--ion-color-${name}-shade`, shade(hex));
  root.style.setProperty(`--ion-color-${name}-tint`, tint(hex));
}

/** Apply the study theme to the document. Idempotent; call whenever tokens change. */
export function applyThemeTokens(tokens: ThemeTokens): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const { colors } = tokens;

  (Object.keys(colors) as (keyof ThemeColorTokens)[]).forEach((role) => {
    root.style.setProperty(`--app-color-${role}`, colors[role]);
    // -rgb enables rgba(var(--app-color-<role>-rgb), a) for translucent uses
    // (progress tracks, overlays); -shade/-tint give darker/lighter variants
    // for pressed states and layered surfaces like the tab bar.
    root.style.setProperty(`--app-color-${role}-rgb`, toRgbString(colors[role]));
    root.style.setProperty(`--app-color-${role}-shade`, shade(colors[role]));
    root.style.setProperty(`--app-color-${role}-tint`, tint(colors[role]));
    // Readable ink (white or dark, WCAG-picked) for text sitting ON this color.
    root.style.setProperty(`--app-color-${role}-contrast`, contrastOn(colors[role]));
  });
  // -contrast-rgb lets chrome consumers (e.g. the tab bar inks) do
  // rgba(var(--app-color-primary-contrast-rgb), a) for softened ink.
  (["primary", "secondary", "accent"] as const).forEach((role) => {
    root.style.setProperty(
      `--app-color-${role}-contrast-rgb`,
      toRgbString(contrastOn(colors[role])),
    );
  });

  // Emphasis — DERIVED role (see deriveEmphasis): the strong tone of primary
  // that progress fills, sliders, completion feedback and emphasized inks use.
  // Consumers read var(--app-color-emphasis, var(--app-color-primary, …)).
  const emphasis = deriveEmphasis(colors.primary);
  root.style.setProperty("--app-color-emphasis", emphasis);
  root.style.setProperty("--app-color-emphasis-tint", tint(emphasis));
  root.style.setProperty("--app-color-emphasis-rgb", toRgbString(emphasis));
  // Ink for content sitting ON the emphasis tone (e.g. the slider handle pin).
  root.style.setProperty("--app-color-emphasis-contrast", contrastOn(emphasis));
  // Pale end of the progress/slider fill gradients. The default 10% tint reads
  // as flat; a 65% tint restores the visible strong->pale sweep the historical
  // Mental+ bar had (#2db6b4 -> #abf0ef).
  root.style.setProperty("--app-color-emphasis-soft", tint(emphasis, 0.65));

  // Page tint — DERIVED: the chrome color fading into the page background (the
  // header flows into the page). Opaque stops ending on the background color, so
  // the LEAVING page never shows through Ionic route transitions (pageStyle.ts).
  const darkChrome = contrastOn(colors.primary) === LIGHT_CONTRAST;
  const pageTint = derivePageTint(colors);
  // The tint stays flat down to the header's bottom (same formula as the
  // WaveToolbar height): ResonanceLayout fills the space behind the header's wavy
  // edge with --app-page-top on tinted pages, so content scrolling under the
  // header disappears cleanly, with no seam where the fade begins.
  const headerBottom = "calc(38vh * (163 / 500))";
  root.style.setProperty("--app-page-top", pageTint.top);
  root.style.setProperty("--app-page-mid", pageTint.mid);
  const pageGradient = `linear-gradient(180deg, ${pageTint.top} 0px, ${pageTint.top} ${headerBottom}, ${pageTint.mid} 300px, ${colors.background} 600px), ${colors.background}`;
  root.style.setProperty("--app-page-background", pageGradient);
  // The same tint for pages that bring their own visuals (Mental+'s island
  // pages): a distinct value, so ResonanceLayout adds no decoration to them.
  root.style.setProperty("--app-page-background-plain", pageGradient);
  // Question screens: the same tint, shorter, and never any decoration.
  root.style.setProperty(
    "--app-question-background",
    `linear-gradient(180deg, ${pageTint.top} 0px, ${pageTint.top} ${headerBottom}, ${pageTint.mid} 200px, ${colors.background} 420px), ${colors.background}`
  );

  // Brand ink — DERIVED: the chrome color darkened until it reads on the tinted
  // page, the cards and the background (icons, links, labels, tonal buttons,
  // card bands, name chips). Raw primary is unreadable as text when pastel.
  const ink = inkOn(colors.primary, [pageTint.top, colors.surface, colors.background]);
  root.style.setProperty("--app-color-ink", ink);
  root.style.setProperty("--app-color-ink-rgb", toRgbString(ink));

  // Round header buttons (logo disc): white on a pale header; on a dark one
  // the shadow would read as a smudge, so it goes.
  root.style.setProperty(
    "--app-header-button-shadow",
    darkChrome ? "none" : `0 6px 14px -8px rgba(${toRgbString(ink)}, 0.55)`
  );

  // Card shape family — a BYPRODUCT of the header edge: flat is the sober
  // choice (tighter corners, rounded-rectangle pills, a thin line instead of a
  // soft shadow); wave / clouds / round are the round family.
  const headerShape = tokens.shape?.header;
  const sober = headerShape === "flat";
  root.style.setProperty("--app-radius-card", sober ? "14px" : "24px");
  root.style.setProperty("--app-radius-answer", sober ? "12px" : "18px");
  root.style.setProperty("--app-radius-pill", sober ? "12px" : "999px");
  root.style.setProperty("--app-radius-chip", sober ? "8px" : "999px");
  // Safety rule: a card (nearly) the same color as the page would disappear, so
  // it gets a thin line (always, in the sober family). 1.1, not "identical":
  // white cards on Outborn's #f8fafc (1.05) lost their top edges, as the soft
  // shadow only shows below a card.
  const needsCardLine = sober || contrastRatio(colors.surface, colors.background) < 1.1;
  root.style.setProperty(
    "--app-card-line",
    needsCardLine ? `rgba(${toRgbString(colors.text)}, 0.1)` : "transparent"
  );
  const textRgb = toRgbString(colors.text);
  const inkRgb = toRgbString(ink);
  root.style.setProperty(
    "--app-shadow-card",
    sober
      ? `0 1px 2px rgba(${textRgb}, 0.05)`
      : `0 1px 2px rgba(${textRgb}, 0.04), 0 16px 34px -24px rgba(${inkRgb}, 0.5)`
  );
  root.style.setProperty(
    "--app-shadow-soft",
    sober
      ? `0 1px 2px rgba(${textRgb}, 0.05)`
      : `0 1px 2px rgba(${textRgb}, 0.05), 0 10px 22px -14px rgba(${inkRgb}, 0.45)`
  );

  // Shape options ride data attributes on <html>; the CSS keys off them
  // (ResonanceLayout header band + page decoration, common.scss tab bar).
  // Setting only the non-default value and REMOVING otherwise keeps
  // re-application idempotent: the default theme paints first, a researcher
  // THEME re-applies on load.
  if (headerShape && headerShape !== "wave") {
    root.setAttribute("data-header-shape", headerShape);
  } else {
    root.removeAttribute("data-header-shape");
  }
  const navbarStyle = tokens.shape?.navbar;
  if (navbarStyle && navbarStyle !== "floating") {
    root.setAttribute("data-navbar-style", navbarStyle);
  } else {
    root.removeAttribute("data-navbar-style");
  }

  const font = fontStack(tokens.font);
  root.style.setProperty("--app-font-family", font);
  root.style.setProperty("--ion-font-family", font);
  // Headings (h1-h6 via App.scss + explicit title components) can carry their
  // own family; absent -> same as the text font, so nothing shifts.
  root.style.setProperty(
    "--app-font-heading",
    tokens.fontTitle ? fontStack(tokens.fontTitle) : font
  );
  // Title weight follows the title font (falls back to the text font).
  const headingFont = tokens.fontTitle ?? tokens.font ?? "";
  root.style.setProperty(
    "--app-font-heading-weight",
    String(HEADING_WEIGHT_BY_FONT[headingFont] ?? 700)
  );

  // Ionic 'primary' is the color IonRadio/IonCheckbox/IonToggle/IonRange etc.
  // render with — under the role semantics those are ANSWER surfaces, so the
  // accent token drives them. 'tertiary' carries the primary (chrome) family
  // for any Ionic consumer that needs it.
  setIonColor(root, "primary", colors.accent);
  setIonColor(root, "secondary", colors.secondary);
  setIonColor(root, "tertiary", colors.primary);
}
